import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotificationStore } from '../../store/notificationStore';
import { notificationService } from '../../services/notificationService';
import { useLanguage } from '../../features/language/useLanguage';
import type { UserNotification } from '../../types/notification';

type TFn = (
  key: string,
  params?: Record<string, string | number>,
  fallback?: string,
) => string;

// Short, glanceable relative label ("now", "3m", "2h", "4d"). Falls back to a
// short date once the event is older than a week.
const formatRelative = (iso: string, now: number, t: TFn): string => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffSec = Math.max(0, Math.floor((now - then) / 1000));
  if (diffSec < 45) return t('notifications.bell.timeNow', undefined, 'now');
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return t('notifications.bell.timeMinutes', { n: diffMin }, `${diffMin}m`);
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return t('notifications.bell.timeHours', { n: diffHr }, `${diffHr}h`);
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return t('notifications.bell.timeDays', { n: diffDay }, `${diffDay}d`);
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatAbsolute = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const BellIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.75}
      d="M15 17h5l-1.4-1.4A2 2 0 0118 14V11a6 6 0 10-12 0v3a2 2 0 01-.6 1.6L4 17h5m6 0a3 3 0 11-6 0"
    />
  </svg>
);

const SkeletonRow: React.FC = () => (
  <div className="px-4 py-3 border-b border-gray-100 last:border-b-0 animate-pulse">
    <div className="flex items-start gap-3">
      <div className="flex-1 space-y-2">
        <div className="h-3 w-3/4 rounded bg-gray-200" />
        <div className="h-2.5 w-full rounded bg-gray-100" />
        <div className="h-2.5 w-2/3 rounded bg-gray-100" />
      </div>
      <div className="h-2.5 w-6 rounded bg-gray-100" />
    </div>
  </div>
);

export const NotificationBell: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const recent = useNotificationStore((s) => s.recent);
  const applyReplay = useNotificationStore((s) => s.applyReplay);
  const markRead = useNotificationStore((s) => s.markRead);
  const setUnreadCount = useNotificationStore((s) => s.setUnreadCount);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());
  const [badgeFlash, setBadgeFlash] = useState(false);
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());
  // Desktop dropdown alignment: prefer right-anchored (grows leftward) but flip
  // to left-anchored when the bell is too close to the header's left edge — e.g.
  // when the app sidebar is expanded and would otherwise cover the panel.
  const [desktopAlign, setDesktopAlign] = useState<'right' | 'left'>('right');

  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const hasLoadedOnce = useRef(false);
  const prevUnread = useRef(unreadCount);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await notificationService.listNotifications({ limit: 10 });
      applyReplay(res.items);
      hasLoadedOnce.current = true;
    } catch {
      setError(t('notifications.bell.loadError', undefined, "Couldn't load notifications"));
    } finally {
      setLoading(false);
    }
  }, [applyReplay, t]);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next) void fetchList();
  };

  const performMarkRead = useCallback(
    async (id: string) => {
      if (pendingIds.has(id)) return;
      const target = recent.find((n) => n.id === id);
      if (!target || target.read_at) return;

      setPendingIds((s) => {
        const next = new Set(s);
        next.add(id);
        return next;
      });
      const prevCount = unreadCount;
      markRead(id);
      try {
        await notificationService.markNotificationRead(id);
      } catch {
        setUnreadCount(prevCount);
      } finally {
        setPendingIds((s) => {
          const next = new Set(s);
          next.delete(id);
          return next;
        });
      }
    },
    [markRead, pendingIds, recent, setUnreadCount, unreadCount],
  );

  const onItemClick = async (n: UserNotification) => {
    const payload = n.payload as Record<string, unknown>;
    const woId = typeof payload.work_order_id === 'string' ? payload.work_order_id : '';
    if (!n.read_at) {
      void performMarkRead(n.id);
      // Tiny pause so the read-state transition registers visually before we navigate.
      await new Promise((r) => window.setTimeout(r, 140));
    }
    setOpen(false);
    if (woId) navigate(`/work-orders?open=${encodeURIComponent(woId)}`);
  };

  const onMarkAll = async () => {
    if (unreadCount === 0) return;
    // Optimistic: flip each in-panel unread to read so the list animates.
    recent.forEach((n) => {
      if (!n.read_at) markRead(n.id);
    });
    try {
      await notificationService.markAllNotificationsRead();
    } catch {
      // Re-sync from server on failure so UI matches truth.
      void fetchList();
    }
  };

  // Measure available horizontal room on the desktop variant. Constrained to the
  // header's own bounds so the panel can't drift under a fixed sidebar.
  const computeDesktopAlign = useCallback(() => {
    const btn = triggerRef.current;
    if (!btn) return;
    // Mobile uses a fixed full-width sheet; skip the measurement entirely.
    if (window.matchMedia('(max-width: 639px)').matches) return;

    const rect = btn.getBoundingClientRect();
    const header = btn.closest('header');
    const bounds = header
      ? header.getBoundingClientRect()
      : { left: 0, right: window.innerWidth };
    const PANEL_PX = 22 * 16; // matches sm:w-[22rem]
    const MARGIN = 8;
    const roomLeft = rect.right - bounds.left - MARGIN; // for right-anchored (grows left)
    const roomRight = bounds.right - rect.left - MARGIN; // for left-anchored (grows right)

    if (roomLeft >= PANEL_PX) {
      setDesktopAlign('right');
    } else if (roomRight >= PANEL_PX) {
      setDesktopAlign('left');
    } else {
      setDesktopAlign(roomRight > roomLeft ? 'left' : 'right');
    }
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    computeDesktopAlign();
    window.addEventListener('resize', computeDesktopAlign);
    return () => window.removeEventListener('resize', computeDesktopAlign);
  }, [open, computeDesktopAlign]);

  // Close on outside click or Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (e.target instanceof Node && rootRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Tick "now" while mounted so relative labels stay fresh.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  // Flash the bell when the unread count increases (live push arrival).
  useEffect(() => {
    if (unreadCount > prevUnread.current) {
      setBadgeFlash(true);
      const id = window.setTimeout(() => setBadgeFlash(false), 420);
      prevUnread.current = unreadCount;
      return () => window.clearTimeout(id);
    }
    prevUnread.current = unreadCount;
  }, [unreadCount]);

  const items = useMemo(() => recent.slice(0, 10), [recent]);
  const hasUnread = unreadCount > 0;
  const showSkeleton = loading && items.length === 0;
  const showError = !loading && error !== null && items.length === 0;
  const showEmpty = !loading && !error && items.length === 0;

  return (
    <div ref={rootRef} className="relative" data-test-id="notification-bell">
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('notifications.bell.title', undefined, 'Notifications')}
        className={[
          'relative inline-flex items-center justify-center p-2 rounded-full',
          'text-gray-600 hover:text-gray-900 hover:bg-gray-100',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 focus-visible:ring-offset-1',
          'transition-colors duration-150',
        ].join(' ')}
      >
        <BellIcon className={`w-5 h-5 ${badgeFlash ? 'animate-shake' : ''}`} />
        {hasUnread && (
          <span
            aria-hidden
            className={[
              'absolute -top-0.5 -right-0.5 inline-flex items-center justify-center',
              'min-w-[18px] h-[18px] px-1 rounded-full',
              'bg-primary-500 text-white text-[10px] font-semibold leading-none tabular-nums',
              'ring-2 ring-white',
              'animate-scale-in',
            ].join(' ')}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Mobile-only scrim: dims background and absorbs taps to close. */}
          <div
            className="fixed inset-0 z-40 bg-slate-900/35 backdrop-blur-[2px] sm:hidden animate-fade-in"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t('notifications.bell.title', undefined, 'Notifications')}
            className={[
              // Mobile: fixed, side-anchored sheet that hangs just below the h-16 header.
              'fixed left-3 right-3 top-[4.5rem] z-50',
              // Desktop: bell-anchored dropdown. Direction picked at open time.
              'sm:absolute sm:inset-x-auto sm:top-full sm:mt-2',
              desktopAlign === 'right'
                ? 'sm:right-0 sm:left-auto sm:origin-top-right'
                : 'sm:left-0 sm:right-auto sm:origin-top-left',
              'sm:w-[22rem] sm:max-w-[calc(100vw-1rem)]',
              // Surface — softer corners + deeper shadow on mobile for the sheet feel.
              'bg-white rounded-2xl sm:rounded-xl border border-gray-200 overflow-hidden',
              'shadow-[0_24px_60px_-16px_rgba(15,23,42,0.4),0_10px_24px_-6px_rgba(15,23,42,0.18)]',
              'sm:shadow-[0_12px_40px_-12px_rgba(15,23,42,0.25),0_4px_12px_-4px_rgba(15,23,42,0.08)]',
              // Motion — slide down on mobile, scale-from-corner on desktop.
              'animate-slide-down sm:animate-scale-in origin-top',
            ].join(' ')}
          >
          <div className="px-4 py-3 flex items-center justify-between border-b border-gray-100">
            <div className="flex items-baseline gap-2 min-w-0">
              <span className="text-sm font-semibold text-gray-900 truncate">
                {t('notifications.bell.title', undefined, 'Notifications')}
              </span>
              {hasUnread && (
                <span className="text-[11px] font-medium text-primary-600 tabular-nums">
                  {t(
                    'notifications.bell.unreadCount',
                    { n: unreadCount },
                    `${unreadCount} unread`,
                  )}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={onMarkAll}
              disabled={!hasUnread}
              className={[
                'text-xs font-medium transition-colors duration-150',
                'focus:outline-none focus-visible:underline',
                hasUnread
                  ? 'text-primary-600 hover:text-primary-700 hover:underline'
                  : 'text-gray-300 cursor-not-allowed',
              ].join(' ')}
            >
              {t('notifications.bell.markAllRead', undefined, 'Mark all read')}
            </button>
          </div>

          <div className="relative max-h-[min(70vh,28rem)] sm:max-h-[24rem] overflow-y-auto overscroll-contain">
            {showSkeleton && (
              <>
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </>
            )}

            {showError && (
              <div className="px-4 py-10 text-center">
                <p className="text-sm text-gray-500">{error}</p>
                <button
                  type="button"
                  onClick={() => void fetchList()}
                  className={[
                    'mt-2 text-xs font-medium text-primary-600 hover:text-primary-700 hover:underline',
                    'focus:outline-none focus-visible:underline',
                  ].join(' ')}
                >
                  {t('notifications.bell.retry', undefined, 'Retry')}
                </button>
              </div>
            )}

            {showEmpty && (
              <div className="px-4 py-10 flex flex-col items-center text-center">
                <span className="w-10 h-10 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center mb-2">
                  <BellIcon className="w-5 h-5" />
                </span>
                <p className="text-sm text-gray-500">
                  {t('notifications.bell.empty', undefined, 'No notifications yet')}
                </p>
              </div>
            )}

            {items.length > 0 && (
              <ul role="menu" className="divide-y divide-gray-100">
                {items.map((n) => {
                  const unread = !n.read_at;
                  const pending = pendingIds.has(n.id);
                  const rel = formatRelative(n.created_at, now, t);
                  const abs = formatAbsolute(n.created_at);
                  return (
                    <li key={n.id} role="none" className="group relative">
                      {/* Left rail: fades out smoothly when the row becomes read. */}
                      <span
                        aria-hidden
                        className={[
                          'absolute left-0 top-0 bottom-0 w-0.5 bg-primary-500',
                          'transition-opacity duration-500 ease-out',
                          unread ? 'opacity-100' : 'opacity-0',
                        ].join(' ')}
                      />
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => void onItemClick(n)}
                        className={[
                          'w-full text-left pl-4 pr-10 py-3',
                          'transition-colors duration-500 ease-out',
                          unread ? 'bg-primary-50/60 hover:bg-primary-50' : 'bg-white hover:bg-gray-50',
                          'focus:outline-none focus-visible:bg-gray-50',
                        ].join(' ')}
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <div
                              className={[
                                'text-sm text-gray-900 truncate',
                                unread ? 'font-semibold' : 'font-medium',
                              ].join(' ')}
                            >
                              {n.title}
                            </div>
                            <div
                              className={[
                                'text-xs mt-0.5 line-clamp-2',
                                'transition-colors duration-500 ease-out',
                                unread ? 'text-gray-700' : 'text-gray-500',
                              ].join(' ')}
                            >
                              {n.body}
                            </div>
                          </div>
                          <div className="shrink-0 flex flex-col items-end gap-1.5">
                            <time
                              dateTime={n.created_at}
                              title={abs}
                              className={[
                                'text-[10px] tabular-nums tracking-wide uppercase',
                                'transition-colors duration-500 ease-out',
                                unread ? 'text-primary-600 font-semibold' : 'text-gray-400',
                              ].join(' ')}
                            >
                              {rel}
                            </time>
                            <span
                              aria-hidden
                              className={[
                                'block w-2 h-2 rounded-full bg-primary-500',
                                'transition-all duration-500 ease-out',
                                unread ? 'opacity-100 scale-100' : 'opacity-0 scale-0',
                              ].join(' ')}
                            />
                          </div>
                        </div>
                      </button>

                      {/* Hover-revealed mark-as-read action: lets the user confirm
                         the read transition without navigating away. */}
                      {unread && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            void performMarkRead(n.id);
                          }}
                          disabled={pending}
                          aria-label={t(
                            'notifications.bell.markOneRead',
                            undefined,
                            'Mark as read',
                          )}
                          className={[
                            'absolute right-2 top-1/2 -translate-y-1/2',
                            'inline-flex items-center justify-center w-7 h-7 rounded-full',
                            'bg-white border border-gray-200 text-gray-500',
                            'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
                            'transition-all duration-150',
                            'hover:text-primary-600 hover:border-primary-300 hover:bg-primary-50',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50',
                            'disabled:opacity-50 disabled:cursor-wait',
                          ].join(' ')}
                        >
                          <svg
                            className="w-3.5 h-3.5"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            aria-hidden
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2.25}
                              d="M5 12l4 4L19 7"
                            />
                          </svg>
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
        </>
      )}
    </div>
  );
};
